import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectSpread2 as _objectSpread2, toPropertyKey as _toPropertyKey } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useRef, useEffect } from 'react';
import { useClick } from '@szhsin/react-menu';
import PropTypes from 'prop-types';
import { StyledMenuButton, StyledControlledMenuContainer, StyledMenuHeader, StyledMenuHeaderButton, StyledMenuDivider, StyledMenuItemTitle, StyledSubMenuItem, StyledMenuItem } from './PopperMenu.styles.js';
const _excluded = ["type", "filterText", "sortText"];
const getMergeProps = (type, props, filterText, sortText) => {
  if (!type) return props;
  const defaults = {
    sort: {
      menuButtonContent: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("i", {
        className: "meta-crm-icon-ic_sort font-size-14",
        style: {
          paddingRight: '5px'
        }
      }), ' ', sortText),
      showTotalSelectedAmount: false,
      menuListTitle: 'Sort',
      menuTitleButton: 'Reset'
    },
    filter: {
      menuButtonContent: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("i", {
        className: "meta-crm-icon-ic_filter2 font-size-14",
        style: {
          paddingRight: '5px'
        }
      }), ' ', filterText),
      showTotalSelectedAmount: true,
      menuListTitle: 'Filter',
      menuTitleButton: 'Clear All'
    }
  };

  // Merge defaults with user-provided props
  return _objectSpread2(_objectSpread2({}, defaults[type]), props);
};
function PopperMenu(_ref) {
  let {
      type,
      filterText,
      sortText
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
    menuButtonContent,
    showTotalSelectedAmount,
    anchorAlign,
    menuListTitle,
    menuTitleButton,
    onMenuTitleButtonClick,
    menuItemGroup,
    selectedItems,
    setSelectedItems,
    totalSelectedAmount,
    setTotalSelectedAmount,
    noBorder
  } = getMergeProps(type, props, filterText, sortText);
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);
  const onClose = () => {
    setIsOpen(false);
  };
  const anchorProps = useClick(isOpen, setIsOpen);
  const calcuateTotalSelectedAmount = startArray => {
    let total = 0;
    let array = startArray;
    if (!Array.isArray(array)) array = [array];
    array.forEach(selectedItem => {
      if (Object.keys(selectedItems).includes(selectedItem)) {
        total += calcuateTotalSelectedAmount(selectedItems[selectedItem]);
      } else {
        total += 1;
      }
    });
    return total;
  };
  useEffect(() => {
    setTotalSelectedAmount(menuItemGroup.reduce((acc, group) => {
      const selectedCount = group.canMultiSelect ? calcuateTotalSelectedAmount(selectedItems[group.menuItemId] || []) : selectedItems[group.menuItemId] || 0;
      return acc + selectedCount;
    }, 0));
  }, [menuItemGroup, selectedItems]);
  const handleOutsideClick = e => {
    if (anchorRef.current && !e.target.closest('.szh-menu')) {
      onClose();
    }
  };
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);
  const handleItemClick = (item, group, canMultiSelect, parentGroup) => {
    const handleSelect = function (itemValue, itemId, menuItemCanMultiSelect, prevState) {
      let isChildren = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
      let isChildrenIdExist = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : true;
      // single select
      if (!menuItemCanMultiSelect) {
        if ((isChildren || !isChildrenIdExist) && prevState[itemId] === itemValue) {
          // unselect action
          const rest = _objectWithoutProperties(prevState, [itemId].map(_toPropertyKey));
          return rest;
        }

        // delete childItem if it's parent is unselected
        if (Object.keys(selectedItems).includes(prevState[itemId])) {
          const _prevState$itemId = prevState[itemId],
            rest = _objectWithoutProperties(prevState, [_prevState$itemId].map(_toPropertyKey));
          return _objectSpread2(_objectSpread2({}, rest), {}, {
            [itemId]: itemValue
          });
        }

        // select action
        return _objectSpread2(_objectSpread2({}, prevState), {}, {
          [itemId]: itemValue
        });
      }
      // multi select
      const updatedSelection = prevState[itemId] || [];
      const newSelection = (isChildren || !isChildrenIdExist) && updatedSelection.includes(itemValue) ? updatedSelection.filter(i => i !== itemValue) // unselect action
      : [...new Set([...updatedSelection, itemValue])]; // select action (avoid duplicates)

      // delete `menuItemId` if it has no value
      if (newSelection.length === 0) {
        const rest = _objectWithoutProperties(prevState, [itemId].map(_toPropertyKey));
        return rest;
      }
      return _objectSpread2(_objectSpread2({}, prevState), {}, {
        [itemId]: newSelection
      });
    };
    if (setSelectedItems) {
      setSelectedItems(prev => {
        const prevItemExist = Boolean(prev[group.menuItemId]);
        const updatedSelection = handleSelect(item.value, group.menuItemId, canMultiSelect, prev);
        const updatedItemExist = Boolean(updatedSelection[group.menuItemId]);

        // When childId apear or disappear
        if (parentGroup && prevItemExist !== updatedItemExist) {
          const updatedParentSelection = parentGroup && handleSelect(group.value, parentGroup.menuItemId, parentGroup.canMultiSelect, updatedSelection, false, updatedItemExist);
          return _objectSpread2({}, updatedParentSelection);
        }
        return _objectSpread2({}, updatedSelection);
      });
    }
    if (group !== null && group !== void 0 && group.onClick) {
      group.onClick(item);
    }
  };
  const renderMenuItems = (items, group, canMultiSelect, parentItem) => items.map((item, index) => {
    var _selectedItems$group$;
    const isSelected = (item === null || item === void 0 ? void 0 : item.checked) || (canMultiSelect ? (_selectedItems$group$ = selectedItems[group.menuItemId]) === null || _selectedItems$group$ === void 0 ? void 0 : _selectedItems$group$.includes(item.value) : selectedItems[group.menuItemId] === item.value) || false;
    const {
      menuItems: subMenuItems,
      canMultiSelect: subMenuCanMultiSelect,
      hasSubMenu
    } = item;
    return hasSubMenu ? /*#__PURE__*/React__default.createElement(StyledSubMenuItem, {
      label: item.label,
      key: index
    }, renderMenuItems(subMenuItems, item, subMenuCanMultiSelect, group)) : /*#__PURE__*/React__default.createElement(StyledMenuItem, {
      key: index,
      onClick: () => handleItemClick(item, group, canMultiSelect, parentItem)
    }, item.label, isSelected && /*#__PURE__*/React__default.createElement("i", {
      className: "meta-crm-icon-ic_check font-size-18",
      style: {
        position: 'absolute',
        right: 10,
        color: '#7B61FF'
      }
    }));
  });
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(StyledMenuButton, _extends({
    noBorder: noBorder,
    type: "button",
    ref: anchorRef
  }, anchorProps, {
    isMenuOpen: isOpen
  }), menuButtonContent, ' ', showTotalSelectedAmount && totalSelectedAmount > 0 && "(".concat(totalSelectedAmount, ")")), /*#__PURE__*/React__default.createElement(StyledControlledMenuContainer, {
    state: isOpen ? 'open' : 'closed',
    anchorRef: anchorRef,
    onClose: () => {},
    align: anchorAlign,
    position: "anchfor"
    // avoid menu overflowing the viewport
    ,

    overflow: "auto",
    boundingBoxPadding: "10 0",
    gap: 4
  }, menuListTitle && /*#__PURE__*/React__default.createElement(StyledMenuHeader, null, /*#__PURE__*/React__default.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }
  }, /*#__PURE__*/React__default.createElement("span", null, menuListTitle), menuTitleButton && /*#__PURE__*/React__default.createElement(StyledMenuHeaderButton, {
    disableRipple: true,
    type: "button",
    onClick: onMenuTitleButtonClick
  }, menuTitleButton))), (menuListTitle || menuTitleButton) && /*#__PURE__*/React__default.createElement(StyledMenuDivider, null), menuItemGroup.map((group, groupIndex) => /*#__PURE__*/React__default.createElement(React__default.Fragment, {
    key: groupIndex
  }, group.menuItemTitle && /*#__PURE__*/React__default.createElement(StyledMenuItemTitle, null, "".concat(group.menuItemTitle, " ").concat(group.canMultiSelect ? "(".concat(calcuateTotalSelectedAmount(selectedItems[group.menuItemId] || []), ")") : '')), renderMenuItems(group.menuItems, group, group.canMultiSelect), groupIndex !== menuItemGroup.length - 1 && /*#__PURE__*/React__default.createElement(StyledMenuDivider, null)))));
}
PopperMenu.propTypes = {
  menuButtonContent: PropTypes.node.isRequired,
  /* eslint-disable react/require-default-props */
  showTotalSelectedAmount: PropTypes.bool,
  anchorAlign: PropTypes.oneOf('start', 'center', 'end'),
  menuListTitle: PropTypes.string,
  menuTitleButton: PropTypes.string,
  onMenuTitleButtonClick: PropTypes.func,
  /* eslint-enable react/require-default-props */
  menuItemGroup: PropTypes.arrayOf(PropTypes.shape({
    menuItemTitle: PropTypes.string.isRequired,
    menuItems: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      canMultiSelect: PropTypes.bool,
      // if has submenu
      /* eslint-disable react/forbid-prop-types */
      menuItems: PropTypes.array,
      // Nested submenu items
      /* eslint-enable react/forbid-prop-types */
      checked: PropTypes.bool
    })),
    canMultiSelect: PropTypes.bool,
    onClick: PropTypes.func.isRequired
  })).isRequired,
  selectedItems: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  setSelectedItems: PropTypes.func,
  totalSelectedAmount: PropTypes.number,
  setTotalSelectedAmount: PropTypes.func,
  type: PropTypes.oneOf([null, 'sort', 'filter']),
  noBorder: PropTypes.bool,
  filterText: PropTypes.string,
  sortText: PropTypes.string
};
PopperMenu.defaultProps = {
  anchorAlign: 'start',
  onMenuTitleButtonClick: () => {},
  selectedItems: {},
  setSelectedItems: () => {},
  type: null,
  totalSelectedAmount: 0,
  setTotalSelectedAmount: () => {},
  noBorder: false,
  filterText: 'Filter',
  sortText: 'Sort'
};
export { PopperMenu as default };